'use client';

import { useState, useCallback, useRef } from 'react';

type Side = 'top' | 'right' | 'bottom' | 'left';

type PositionStyle = {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
};

type UsePositioningResult = {
  /** The actual side after collision detection (may differ from preferred) */
  computedSide: Side;
  /** Inline styles to position the floating element */
  positionStyle: PositionStyle;
  /** Inline styles to position the arrow element */
  arrowStyle: PositionStyle;
  /** Call this to recalculate position (e.g. when tooltip becomes visible) */
  updatePosition: () => void;
  /** Ref to attach to the wrapper element */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to attach to the floating element (tooltip/popover) */
  floatingRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Hook for positioning floating elements (tooltips, popovers) with
 * collision detection. Flips to the opposite side when there isn't
 * enough room, and shifts horizontally/vertically to stay in viewport.
 *
 * @param options - Positioning configuration
 * @returns Position data, refs, and an update function
 *
 * @example
 * const { computedSide, positionStyle, arrowStyle, updatePosition, wrapperRef, floatingRef } =
 *   usePositioning({ side: 'top', gap: 8 });
 */
type UsePositioningOptions = {
  /** Preferred placement side */
  side?: Side;
  /** Gap between trigger and floating element in px */
  gap?: number;
  /** Padding from viewport edges in px */
  viewportPadding?: number;
  /** Whether positioning is active */
  enabled?: boolean;
  /**
   * Positioning strategy for the floating element.
   * - 'absolute' (default): coordinates are relative to the wrapper element.
   * - 'fixed': coordinates are relative to the viewport, so the floating
   *   element escapes ancestors with `overflow: hidden` (e.g. inside tables).
   */
  strategy?: 'absolute' | 'fixed';
};

/** Amount to shift a floating element back into the viewport along one axis. */
const computeShift = (
  start: number,
  size: number,
  viewportSize: number,
  padding: number
): number => {
  const end = start + size;
  if (start < padding) return padding - start;
  if (end > viewportSize - padding) return viewportSize - padding - end;
  return 0;
};

/** Flip a top/bottom placement to the opposite side when there isn't room. */
const flipVertical = (side: Side, spaceAbove: number, spaceBelow: number, needed: number): Side => {
  if (side === 'top' && spaceAbove < needed && spaceBelow > spaceAbove) return 'bottom';
  if (side === 'bottom' && spaceBelow < needed && spaceAbove > spaceBelow) return 'top';
  return side;
};

/** Flip a left/right placement to the opposite side when there isn't room. */
const flipHorizontal = (
  side: Side,
  spaceLeft: number,
  spaceRight: number,
  needed: number
): Side => {
  if (side === 'left' && spaceLeft < needed && spaceRight > spaceLeft) return 'right';
  if (side === 'right' && spaceRight < needed && spaceLeft > spaceRight) return 'left';
  return side;
};

/** Resolve the final placement side after collision detection. */
const resolveSide = (
  side: Side,
  triggerRect: DOMRect,
  floatingRect: DOMRect,
  gap: number,
  viewportWidth: number,
  viewportHeight: number
): Side => {
  if (side === 'top' || side === 'bottom') {
    return flipVertical(
      side,
      triggerRect.top,
      viewportHeight - triggerRect.bottom,
      floatingRect.height + gap
    );
  }
  return flipHorizontal(
    side,
    triggerRect.left,
    viewportWidth - triggerRect.right,
    floatingRect.width + gap
  );
};

type Placement = { newStyle: PositionStyle; newArrowStyle: PositionStyle };

/** Compute styles for a top/bottom placement (centered horizontally). */
const placeVertical = (
  resolvedSide: Side,
  triggerRect: DOMRect,
  floatingWidth: number,
  floatingHeight: number,
  gap: number,
  viewportWidth: number,
  viewportPadding: number,
  strategy: 'absolute' | 'fixed'
): Placement => {
  const idealLeft = (triggerRect.width - floatingWidth) / 2;
  const shiftX = computeShift(
    triggerRect.left + idealLeft,
    floatingWidth,
    viewportWidth,
    viewportPadding
  );
  const finalLeft = idealLeft + shiftX;

  const newStyle: PositionStyle = {};
  if (strategy === 'fixed') {
    newStyle.left = `${triggerRect.left + finalLeft}px`;
    newStyle.top =
      resolvedSide === 'top'
        ? `${triggerRect.top - floatingHeight - gap}px`
        : `${triggerRect.bottom + gap}px`;
  } else {
    newStyle.left = `${finalLeft}px`;
    if (resolvedSide === 'top') {
      newStyle.bottom = `calc(100% + ${gap}px)`;
    } else {
      newStyle.top = `calc(100% + ${gap}px)`;
    }
  }

  const arrowLeft = floatingWidth / 2 - 4 - shiftX; // 4 = arrow half-size
  const newArrowStyle: PositionStyle = {
    left: `${Math.max(8, Math.min(arrowLeft, floatingWidth - 16))}px`,
  };
  return { newStyle, newArrowStyle };
};

/** Compute styles for a left/right placement (centered vertically). */
const placeHorizontal = (
  resolvedSide: Side,
  triggerRect: DOMRect,
  floatingWidth: number,
  floatingHeight: number,
  gap: number,
  viewportHeight: number,
  viewportPadding: number,
  strategy: 'absolute' | 'fixed'
): Placement => {
  const idealTop = (triggerRect.height - floatingHeight) / 2;
  const shiftY = computeShift(
    triggerRect.top + idealTop,
    floatingHeight,
    viewportHeight,
    viewportPadding
  );
  const finalTop = idealTop + shiftY;

  const newStyle: PositionStyle = {};
  if (strategy === 'fixed') {
    newStyle.top = `${triggerRect.top + finalTop}px`;
    newStyle.left =
      resolvedSide === 'left'
        ? `${triggerRect.left - floatingWidth - gap}px`
        : `${triggerRect.right + gap}px`;
  } else {
    newStyle.top = `${finalTop}px`;
    if (resolvedSide === 'left') {
      newStyle.right = `calc(100% + ${gap}px)`;
    } else {
      newStyle.left = `calc(100% + ${gap}px)`;
    }
  }

  const arrowTop = floatingHeight / 2 - 4 - shiftY;
  const newArrowStyle: PositionStyle = {
    top: `${Math.max(8, Math.min(arrowTop, floatingHeight - 16))}px`,
  };
  return { newStyle, newArrowStyle };
};

const usePositioning = ({
  side = 'top',
  gap = 8,
  viewportPadding = 8,
  enabled = true,
  strategy = 'absolute',
}: UsePositioningOptions = {}): UsePositioningResult => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const floatingRef = useRef<HTMLDivElement | null>(null);

  const [computedSide, setComputedSide] = useState<Side>(side);
  const [positionStyle, setPositionStyle] = useState<PositionStyle>({});
  const [arrowStyle, setArrowStyle] = useState<PositionStyle>({});

  const updatePosition = useCallback(() => {
    if (!enabled || !wrapperRef.current || !floatingRef.current) return;

    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const floatingRect = floatingRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Determine the best side (flip if not enough space)
    const resolvedSide = resolveSide(
      side,
      triggerRect,
      floatingRect,
      gap,
      viewportWidth,
      viewportHeight
    );
    setComputedSide(resolvedSide);

    const { newStyle, newArrowStyle } =
      resolvedSide === 'top' || resolvedSide === 'bottom'
        ? placeVertical(
            resolvedSide,
            triggerRect,
            floatingRect.width,
            floatingRect.height,
            gap,
            viewportWidth,
            viewportPadding,
            strategy
          )
        : placeHorizontal(
            resolvedSide,
            triggerRect,
            floatingRect.width,
            floatingRect.height,
            gap,
            viewportHeight,
            viewportPadding,
            strategy
          );

    setPositionStyle(newStyle);
    setArrowStyle(newArrowStyle);
  }, [side, gap, viewportPadding, enabled, strategy]);

  return {
    computedSide,
    positionStyle,
    arrowStyle,
    updatePosition,
    wrapperRef,
    floatingRef,
  };
};

export default usePositioning;
